import {
  Component,
  createRef,
  forwardRef,
  MutableRefObject,
  Ref,
  RefObject,
} from "react";
import * as React from "react";
import ReactDOM from "react-dom";
import cn from "classnames";
import { createBemBlockBuilder, GetBemBlocksWith } from "@ebsco-ui/utils";

import "./drawer.scss";

enum INTERACTIONS_AFFORDANCE {
  ESC = "Escape key",
  OVERLAY = "Overlay",
}

enum POSITIONS {
  start = "start",
  end = "end",
}

const FOCUSABLE_ELEMENTS_SELECTORS = [
  "button",
  "[href]",
  "input",
  "select",
  "textarea",
  "[tabindex]:not([tabindex='-1'])",
].join(", ");

enum KEYS {
  ESCAPE = "Escape",
  TAB = "Tab",
}

export interface DrawerProps {
  disableAnimations?: boolean;
  children: React.ReactNode;
  className?: string;
  isGlobal?: boolean;
  isOpen?: boolean;
  onClose(affordance: string | undefined): void;
  position: keyof typeof POSITIONS;
  setFocusOnOpen?: boolean;
  setFocusOnClose?: boolean;
  showOverlay?: boolean;
  titleElementId: string;
  returnFocusSelector?: string;
  drawerTag?: keyof Pick<JSX.IntrinsicElements, "div" | "section" | "aside">;
  zIndex?: number;
}

const baseClassName = "eb-drawer";

const defaultZIndex = 9000;

class DrawerNoRef extends Component<
  DrawerProps & { forwardedRef: Ref<HTMLDivElement> }
> {
  static defaultProps = {
    disableAnimations: false,
    isOpen: false,
    setFocusOnOpen: true,
    setFocusOnClose: true,
    showOverlay: true,
    isGlobal: true,
    className: "",
    position: "start",
    returnFocusSelector: "",
  };

  drawerRef: RefObject<HTMLDivElement>;

  getBemBlocksWith: GetBemBlocksWith;

  private initiallyFocusedElement?: Element | null;

  constructor(props: DrawerProps & { forwardedRef: Ref<HTMLDivElement> }) {
    super(props);
    this.drawerRef = createRef();
    if (props.isOpen) {
      this.handleOpen();
    }

    this.getBemBlocksWith = createBemBlockBuilder([
      baseClassName,
      this.props.className,
    ]);
  }

  drawerRefCallback = (node: HTMLDivElement) => {
    const { forwardedRef } = this.props;
    if (forwardedRef) {
      if (typeof forwardedRef === "function") {
        forwardedRef(node);
      } else {
        (forwardedRef as MutableRefObject<HTMLDivElement>).current = node;
      }
    }
    (this.drawerRef as MutableRefObject<HTMLDivElement>).current = node;
  };

  componentDidUpdate(prevProps: DrawerProps) {
    const isOpening = !prevProps.isOpen && this.props.isOpen;
    const isClosing = prevProps.isOpen && !this.props.isOpen;
    if (isOpening) {
      this.handleOpen();
    }
    if (isClosing) {
      this.handleClose(true);
    }
  }

  componentWillUnmount() {
    document.removeEventListener("keydown", this.handleKeyDown);
  }

  focusOnContent = () => {
    let focusable;

    if (this.drawerRef.current) {
      focusable = this.drawerRef.current.querySelector<HTMLElement>(
        FOCUSABLE_ELEMENTS_SELECTORS,
      );
    }

    if (focusable) {
      focusable.focus();
    }
  };

  handleKeyDown = (event: KeyboardEvent) => {
    if (!this.props.isGlobal) {
      return;
    }

    const focusableElements = this.drawerRef.current
      ? this.drawerRef.current.querySelectorAll<HTMLElement>(
          FOCUSABLE_ELEMENTS_SELECTORS,
        )
      : [];
    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];
    const activeElement = document.activeElement;
    if (event.key === KEYS.ESCAPE) {
      this.handleClose(false, INTERACTIONS_AFFORDANCE.ESC);
    } else if (event.key === KEYS.TAB && event.shiftKey) {
      if (activeElement === firstFocusable) {
        lastFocusable.focus();
        event.preventDefault();
      }
    } else if (event.key === KEYS.TAB && activeElement === lastFocusable) {
      firstFocusable.focus();
      event.preventDefault();
    }
  };

  handleOpen = () => {
    const { setFocusOnOpen } = this.props;

    document.addEventListener("keydown", this.handleKeyDown);
    this.initiallyFocusedElement = document.activeElement;

    if (setFocusOnOpen) {
      /* With styles we have it requires waiting two animation frames (requestAnimationFrame cycles)
      before you can focus on an element, so without timeout focus won't be set.
      When delay is less when 400 content will glitch in some cases. See US574068 for issue description.*/
      const ANIMATION_DELAY = 400;
      setTimeout(this.focusOnContent, ANIMATION_DELAY);
    }
  };

  handleClose = (propsChanged: boolean, affordance?: string) => {
    const { returnFocusSelector, setFocusOnClose, onClose } = this.props;

    if (setFocusOnClose) {
      const elementToFocus = returnFocusSelector
        ? document.querySelector(returnFocusSelector)
        : this.initiallyFocusedElement;

      if (elementToFocus) {
        (elementToFocus as HTMLElement).focus();
      }
    }

    document.removeEventListener("keydown", this.handleKeyDown);
    if (!propsChanged) {
      onClose(affordance);
    }
  };

  handleOverlayClick = () => {
    this.handleClose(false, INTERACTIONS_AFFORDANCE.OVERLAY);
  };

  renderOverlay = () => {
    const {
      disableAnimations,
      isOpen,
      isGlobal,
      showOverlay,
      zIndex = defaultZIndex,
    } = this.props;
    if (!showOverlay) {
      return null;
    }

    const overlayClassNames = cn(
      this.getBemBlocksWith("__overlay"),
      disableAnimations &&
        this.getBemBlocksWith("__overlay--disable-animations"),
      !isGlobal && this.getBemBlocksWith("__overlay--contained"),
      isOpen && this.getBemBlocksWith("__overlay--open"),
    );
    return (
      <div
        className={overlayClassNames}
        data-auto="drawer-overlay"
        onClick={this.handleOverlayClick}
        style={{ zIndex }}
      />
    );
  };

  renderDrawer = () => {
    const {
      disableAnimations,
      isOpen,
      isGlobal,
      position,
      className,
      children,
      titleElementId,
      drawerTag: DrawerTag = "div",
      zIndex = defaultZIndex,
    } = this.props;
    const drawerClassNames = cn(
      this.getBemBlocksWith(),
      disableAnimations && this.getBemBlocksWith("--disable-animations"),
      className,
      !isGlobal && this.getBemBlocksWith("--contained"),
      isOpen && this.getBemBlocksWith("--open"),
      position === POSITIONS.start &&
        this.getBemBlocksWith("--start-positioned"),
      position === POSITIONS.end && this.getBemBlocksWith("--end-positioned"),
    );

    return (
      <DrawerTag
        className={drawerClassNames}
        style={{ zIndex: zIndex + 1 }}
        ref={this.drawerRefCallback}
        aria-hidden={!isOpen}
        aria-labelledby={titleElementId}
        data-auto="drawer"
        tabIndex={-1}
      >
        {children}
      </DrawerTag>
    );
  };

  render() {
    const { isGlobal } = this.props;
    // It appears that document can be undefined initially in case of server-side rendering.
    // Would appreciate any suggestions for more elegant workaround
    if (typeof document === "undefined") {
      return null;
    }
    const drawer = (
      <>
        {this.renderOverlay()}
        {this.renderDrawer()}
      </>
    );

    return isGlobal ? ReactDOM.createPortal(drawer, document.body) : drawer;
  }
}

const Drawer = forwardRef<HTMLDivElement, DrawerProps>(
  (props: DrawerProps, ref) => {
    return <DrawerNoRef {...props} forwardedRef={ref} />;
  },
);

Drawer.displayName = "Drawer";

export default Drawer;
